// EASIUR engine result table — county-level monetised damages.
//
// Today's payload (from `inferno/engines/easiur.py::query_easiur_for_plants`):
//   { ozone_usd, pec_pm_usd, so2_pm_usd, nh3_pm_usd, nox_pm_usd }
// (all flat USD totals across all plants; no per-county breakdown yet).
//
// When the engine grows a `counties` array (Track C9 follow-on), the
// table renders the county breakdown instead. Either way, USD totals
// per damage category are always shown so the user gets *something*.

import React from "react";
import { toSigFigs } from "../../utils/format";

interface EasiurEngineTableProps {
  payload: Record<string, any>;
}

const DAMAGE_LABELS: Record<string, string> = {
  ozone_usd: "Ozone (NOx → O₃)",
  pec_pm_usd: "Primary PM₂.₅ (PEC)",
  so2_pm_usd: "Secondary PM (SO₂)",
  nh3_pm_usd: "Secondary PM (NH₃)",
  nox_pm_usd: "Secondary PM (NOx)",
};

export function EasiurEngineTable({ payload }: EasiurEngineTableProps) {
  const counties = Array.isArray(payload?.counties) ? payload.counties : null;
  const totalUsd =
    typeof payload?.total_usd === "number"
      ? payload.total_usd
      : Object.entries(payload || {})
          .filter(([k, v]) => k.endsWith("_usd") && typeof v === "number")
          .reduce((acc, [, v]) => acc + (v as number), 0);

  const damageRows = Object.entries(payload || {})
    .filter(([k, v]) => k.endsWith("_usd") && k !== "total_usd" && typeof v === "number")
    .map(([k, v]) => ({
      key: k,
      label: DAMAGE_LABELS[k] ?? k.replace(/_/g, " "),
      usd: v as number,
    }));

  if (damageRows.length === 0 && !counties) {
    return (
      <p className="text-sm text-text-muted">
        EASIUR ran but recorded no damages — likely because no plants had a resolved county FIPS.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
        <div className="bg-surface-800 rounded p-3">
          <div className="text-xs text-text-muted uppercase tracking-wider">Total damages</div>
          <div className="text-base text-text-primary mt-1">${toSigFigs(totalUsd)} / yr</div>
        </div>
      </div>

      {damageRows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-surface-600">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
                  Damage category
                </th>
                <th className="px-4 py-2 text-right text-xs font-medium text-text-muted uppercase tracking-wider">
                  USD / yr
                </th>
                <th className="px-4 py-2 text-right text-xs font-medium text-text-muted uppercase tracking-wider">
                  % of total
                </th>
              </tr>
            </thead>
            <tbody className="bg-surface-700 divide-y divide-border">
              {damageRows.map((row) => (
                <tr key={row.key} className="hover:bg-surface-600 transition-colors">
                  <td className="px-4 py-2 text-sm font-medium text-text-primary">{row.label}</td>
                  <td className="px-4 py-2 text-right text-sm text-text-secondary">
                    ${toSigFigs(row.usd)}
                  </td>
                  <td className="px-4 py-2 text-right text-sm text-text-secondary">
                    {totalUsd > 0 ? `${((row.usd / totalUsd) * 100).toFixed(1)}%` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {counties && counties.length > 0 && (
        <div>
          <h5 className="text-sm font-medium text-text-primary mb-2">By county</h5>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-surface-600">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
                    FIPS
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
                    County
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-text-muted uppercase tracking-wider">
                    USD / yr
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-text-muted uppercase tracking-wider">
                    % of total
                  </th>
                </tr>
              </thead>
              <tbody className="bg-surface-700 divide-y divide-border">
                {counties.slice(0, 25).map((c: any, idx: number) => (
                  <tr key={c?.fips ?? idx} className="hover:bg-surface-600 transition-colors">
                    <td className="px-4 py-2 text-sm text-text-secondary">{c?.fips ?? "—"}</td>
                    <td className="px-4 py-2 text-sm text-text-secondary">{c?.name ?? "—"}</td>
                    <td className="px-4 py-2 text-right text-sm text-text-secondary">
                      {typeof c?.usd === "number" ? `$${toSigFigs(c.usd)}` : "—"}
                    </td>
                    <td className="px-4 py-2 text-right text-sm text-text-secondary">
                      {typeof c?.usd === "number" && totalUsd > 0
                        ? `${((c.usd / totalUsd) * 100).toFixed(1)}%`
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
